import { NgModule, LOCALE_ID } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { MsalGuard, MsalBroadcastService, MsalModule, MsalService, MsalInterceptor, MsalRedirectComponent } from '@azure/msal-angular';

import './global';
import { AppRoutingModule } from './app-routing.module';
import { EkHttpInterceptor } from './interceptors/http.interceptor';
import { AppComponent } from './app.component';
import { ImprintComponent } from './extras/imprint/imprint.component';
import { PrivacyComponent } from './extras/privacy/privacy.component';
import { LoginComponent } from './extras/login/login.component';
import { ContainerComponent } from './pages/container/container.component';
import { MainComponent } from './common/main/main.component';
import { AdminMainComponent } from './pages/admin/main/main.component';
import { CustomersComponent } from './pages/admin/customers/customers.component';
import { SettingsComponent } from './common/settings/settings.component';
import { CustomerDetailComponent } from './pages/admin/customers/customer-detail/customer-detail.component';
import { GenericModalComponent } from './components/modals/generic-modal/generic-modal.component';
import { PromptModalComponent } from './components/modals/prompt-modal/prompt-modal.component';
import { SuppliersComponent } from './pages/admin/suppliers/suppliers.component';
import { SupplierDetailComponent } from './pages/admin/suppliers/supplier-detail/supplier-detail.component';
import { EmployeesComponent } from './components/employees/employees.component';
import { DataStreamsComponent } from './pages/admin/data/data-streams/data-streams.component';
import { DataStreamOverviewComponent } from './pages/admin/customers/customer-detail/data-stream-overview/data-stream-overview.component';
import { MediaSearchComponent } from './pages/admin/data/media-search/media-search.component';
import { TranslateDirective, TranslatePipe, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { JobsComponent } from './pages/admin/data/jobs/jobs.component';
import { SupplierProductDetailComponent } from './pages/admin/data/supplier-product-detail/supplier-product-detail.component';
import { SupplierProductSearchComponent } from './pages/admin/data/supplier-product-search/supplier-product-search.component';
import { RecoverPasswordComponent } from './extras/recover-password/recover-password.component';
import { CreateUserFormComponent } from './pages/admin/users/create-user-form/create-user-form.component';
import { ApiKeysComponent } from './components/api-keys/api-keys.component';
import { CockpitComponent as SupplierCockpitComponent } from './pages/supplier/cockpit/cockpit.component';
import { CockpitComponent as CustomerCockpitComponent } from './pages/customer/cockpit/cockpit.component';
import { PasswordChangeFormComponent } from './components/password/password-change-form/password-change-form.component';
import { DataManagementComponent } from './components/data-management/data-management.component';
import { DataManagementStatusComponent } from '@components/data-management/status/status.component';
import { DataManagementUploadComponent } from '@components/data-management/upload/upload.component';
import { DataManagementFtpComponent } from './components/ftp-info/ftp.component';
import { DataManagementActionsComponent } from '@components/data-management/actions/actions.component';
import { MessagesComponent } from './components/data-management/upload/messages/messages.component';
import { EmployeeComponent } from './components/employees/employee/employee.component';
import { NameFormLegacyComponent } from './components/name/name-form-legacy/name-form-legacy.component';
import { EmailFormLegacyComponent } from './components/email/email-form-legacy/email-form-legacy.component';
import { VerifyEmailComponent } from './components/email/verify-email/verify-email.component';
import { MessagesNavComponent } from './components/data-management/upload/messages-nav/messages-nav.component';
import { PasswordStrengthComponent } from './components/password-strength/password-strength.component';
import { DataStreamGroupComponent } from './pages/customer/cockpit/data-stream-group/data-stream-group.component';
import { DataStreamComponent } from './pages/customer/cockpit/data-stream-group/data-stream/data-stream.component';
import { RequestDataStreamComponent } from './pages/customer/cockpit/request-data-stream/request-data-stream.component';
import { SortableHeader } from './directives/sortable-header';
import { IdentificationPipe } from './pipes/identification.pipe';
import { DataApprovalsComponent } from './components/data-approvals/data-approvals.component';
import { StatusFilterPipe } from './pipes/status-filter.pipe';
import { DatastreamFilterPipe } from './pipes/datastream-filter.pipe';
import { SupplierContactComponent } from './pages/supplier/cockpit/contact/contact.component';
import { AutoApproveComponent } from './pages/supplier/cockpit/auto-approve/auto-approve.component';
import { environment } from '@envs/environment';
import { OrderAccessesComponent } from './components/order-accesses/order-accesses.component';
import { DeliveryConditionsComponent } from './components/delivery-conditions/delivery-conditions.component';
import { SupplierHeaderImageUploadComponent } from './components/supplier-header-image-upload/supplier-header-image-upload.component';
import { LegacyProfileComponent } from './common/settings/legacy-profile/legacy-profile.component';
import { SsoSignupModalComponent } from './components/sso-signup-modal/sso-signup-modal.component';
import { PartyRelPipe } from './pipes/party-rel.pipe';
import { SsoComponent } from './pages/sso/sso.component';
import { OtherPartyRelsPipe } from './pipes/other-party-rels.pipe';
import { PasswordResetComponent } from './pages/sso/password-reset/password-reset.component';
import { provideMatomo, withRouter } from 'ngx-matomo-client';
import { HttpService } from '@services/http.service';
import { ErrorService } from '@services/error.service';
import { UserService } from '@services/user.service';
import { PdfCatalogsComponent } from './pages/admin/data/pdf-catalogs/pdf-catalogs.component';

registerLocaleData(localeDe);

const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1; // Remove this line to use Angular Universal

@NgModule({
  declarations: [
    AppComponent,
    ImprintComponent,
    PrivacyComponent,
    LoginComponent,
    ContainerComponent,
    MainComponent,
    AdminMainComponent,
    CustomersComponent,
    SettingsComponent,
    CustomerDetailComponent,
    GenericModalComponent,
    PromptModalComponent,
    SuppliersComponent,
    SupplierDetailComponent,
    EmployeesComponent,
    DataStreamsComponent,
    DataStreamOverviewComponent,
    MediaSearchComponent,
    JobsComponent,
    SupplierProductDetailComponent,
    SupplierProductSearchComponent,
    RecoverPasswordComponent,
    CreateUserFormComponent,
    ApiKeysComponent,
    SupplierCockpitComponent,
    CustomerCockpitComponent,
    PasswordChangeFormComponent,
    DataManagementComponent,
    DataManagementStatusComponent,
    DataManagementUploadComponent,
    DataManagementActionsComponent,
    DataManagementFtpComponent,
    MessagesComponent,
    EmployeeComponent,
    NameFormLegacyComponent,
    EmailFormLegacyComponent,
    VerifyEmailComponent,
    MessagesNavComponent,
    PasswordStrengthComponent,
    DataStreamGroupComponent,
    DataStreamComponent,
    RequestDataStreamComponent,
    SortableHeader,
    IdentificationPipe,
    IdentificationPipe,
    DataApprovalsComponent,
    StatusFilterPipe,
    DatastreamFilterPipe,
    SupplierContactComponent,
    AutoApproveComponent,
    OrderAccessesComponent,
    DeliveryConditionsComponent,
    SupplierHeaderImageUploadComponent,
    LegacyProfileComponent,
    SsoSignupModalComponent,
    PartyRelPipe,
    SsoComponent,
    OtherPartyRelsPipe,
    PasswordResetComponent,
    PdfCatalogsComponent,
  ],
  bootstrap: [
    AppComponent,
    MsalRedirectComponent,
  ],
  imports: [
    MsalModule.forRoot(new PublicClientApplication({ // MSAL Configuration
      auth: {
        clientId: environment.sso.clientId,
        authority: `https://${environment.sso.tenant}.b2clogin.com/${environment.sso.tenant}.onmicrosoft.com/B2C_1A_SIGNIN`,
        knownAuthorities: [`${environment.sso.tenant}.b2clogin.com`],
        redirectUri: environment.sso.redirectUri,
      },
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage,
      },
      system: {
        loggerOptions: {
          loggerCallback: (logLevel, message) => {
            if (!environment.production) {
              console.log(LogLevel[logLevel], message);
            }
          },
          piiLoggingEnabled: false
        }
      }
    }), {
      interactionType: InteractionType.Redirect, // MSAL Guard Configuration
      authRequest: {
        scopes: [`https://${environment.sso.tenant}.b2clogin.com/user.read`],
      },
      loginFailedRoute: '/login',
    }, {
      interactionType: InteractionType.Redirect, // MSAL Interceptor Configuration
      protectedResourceMap: new Map([
        ["https://graph.microsoft.com/v1.0/me", [`https://${environment.sso.tenant}.b2clogin.com/user.read`]]
      ]),
    }),
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModule,
    FontAwesomeModule,
    TranslatePipe,
    TranslateDirective,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: EkHttpInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'de-DE' },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    HttpService,
    ErrorService,
    UserService,
    provideHttpClient(withInterceptorsFromDi()),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json',
        useHttpBackend: true,
      }),
      fallbackLang: 'de',
    }),
    provideMatomo(
      {
        disabled: !environment.matomo.enabled,
        siteId: environment.matomo.siteId,
        trackerUrl:  environment.matomo.serverUrl,
      },
      withRouter(),
    ),
  ]
})
export class AppModule {
  constructor(library: FaIconLibrary) {
    library.addIconPacks(fas, far);
  }
}
